import React from 'react';

import { config, moment } from 'data';
import { dateRange, formatter } from 'helpers';
import { useForm, useFormWatch, useLang } from 'hooks';
import { useStatisticsPaymentsQuery } from 'hooks/queries';
import { Highcharts } from 'components/layout';
import { Form, Select } from 'components/ui';
import { DateRange } from 'types/views';

import ChartView from './ChartView';

const initialRange = DateRange.THIS_WEEK;
const initialDates = dateRange.toDates(initialRange);

type FilterValues = {
  range?: DateRange;
};

const initialFilterValues: FilterValues = {
  range: initialRange,
};

const IncomingPaymentsChart = () => {
  const filterForm = useForm<FilterValues>();
  const lang = useLang();

  const currentRange = useFormWatch('range', filterForm);

  const dates = currentRange && dateRange.toDates(currentRange);
  const dateFrom = dates?.[0] ?? initialDates[0];
  const dateTo = dates?.[1] ?? initialDates[1];

  const paymentsQuery = useStatisticsPaymentsQuery({ dateFrom, dateTo });

  return (
    <ChartView
      title={lang.get('dashboard.chart.incomingPayments')}
      currency={config.DEFAULT_CURRENCY}
      filter={(
        <Form.Item name="range">
          <Select
            placeholder={lang.get('common.filters.range')}
            options={Object.values(DateRange).map((range) => ({
              value: range,
              label: lang.get(`common.dateRanges.${range.toLowerCase()}`),
            }))}
            popupMatchSelectWidth={false}
          />
        </Form.Item>
      )}
      filterProps={{
        form: filterForm,
        initialValues: initialFilterValues,
      }}
      loading={paymentsQuery.isLoading}
    >
      <Highcharts
        options={{
          xAxis: {
            type: 'datetime',
            tickLength: 0,
            labels: {
              step: 2,
              formatter: (context) => moment(context.value).format(config.DATE_MONTH_FORMAT),
            },
          },
          series: [
            {
              type: 'column',
              borderRadius: 2,
              enableMouseTracking: false,
              minPointLength: 0.01,
              maxPointWidth: 32,
              data: paymentsQuery.data?.map((payment) => [
                moment.utc(payment.day).valueOf(),
                formatter.transformFromCents(payment.amount),
              ]),
            },
          ],
        }}
      />
    </ChartView>
  );
};

export default IncomingPaymentsChart;
